import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

// Real AI-generated scenes via Nano Banana · served via backend cache
const IMG = {
    hero: `${API}/api/yatra/scene/hero`,
    kashi: `${API}/api/yatra/scene/kashi`,
    himalaya: `${API}/api/yatra/scene/himalaya`,
    meditation: `${API}/api/yatra/scene/meditation`,
    diya: `${API}/api/yatra/scene/diya`,
    temple: `${API}/api/yatra/scene/temple`,
    ganga: `${API}/api/yatra/scene/ganga`,
    mustard: `${API}/api/yatra/scene/mustard`,
    chart: `${API}/api/yatra/scene/chart`,
    kedarnath: `${API}/api/yatra/scene/kedarnath`,
    badrinath: `${API}/api/yatra/scene/badrinath`,
    gangotri: `${API}/api/yatra/scene/gangotri`,
    yamunotri: `${API}/api/yatra/scene/yamunotri`,
};

// Gradient fallback if image fails to load
const gradientBg = (from, to) => ({
    background: `linear-gradient(135deg, ${from}, ${to})`,
});
const FALLBACK_GRADIENTS = {
    hero: gradientBg("#4a1810", "#000"),
    kashi: gradientBg("#8b3a1a", "#1a0a05"),
    himalaya: gradientBg("#1e3a5f", "#050510"),
    meditation: gradientBg("#3d2e1a", "#0a0805"),
    diya: gradientBg("#c67c1a", "#2a1005"),
    temple: gradientBg("#6b2a1a", "#100505"),
    ganga: gradientBg("#4a4515", "#0a0a05"),
    mustard: gradientBg("#c9a129", "#3a2a05"),
    chart: gradientBg("#2a1a4d", "#050510"),
    kedarnath: gradientBg("#2a3f5c", "#0a0a15"),
    badrinath: gradientBg("#8b4515", "#1a0805"),
    gangotri: gradientBg("#3d5a6b", "#0a1015"),
    yamunotri: gradientBg("#3d4a2a", "#0a0f05"),
};

const Section = ({ children, className = "" }) => (
    <section className={`w-full py-20 md:py-28 px-6 md:px-12 ${className}`}>
        <div className="max-w-6xl mx-auto">{children}</div>
    </section>
);

const Reveal = ({ children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
        {children}
    </motion.div>
);

const Tag = ({ children }) => (
    <span className="inline-block px-3 py-1 rounded-full text-[10px] tracking-[0.3em] uppercase bg-amber-500/10 text-amber-300 border border-amber-500/25">
        {children}
    </span>
);

const NumCard = ({ n, label, sub }) => (
    <div className="p-5 rounded-2xl bg-neutral-950/80 backdrop-blur border border-neutral-800">
        <div className="text-3xl md:text-4xl font-serif text-amber-200">{n}</div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
        {sub && <div className="text-xs text-neutral-500 mt-2">{sub}</div>}
    </div>
);

const PillarCard = ({ tag, title, sub, image, imageKey, points }) => (
    <div className="rounded-3xl overflow-hidden bg-neutral-950 border border-neutral-800 group">
        <div className="relative h-64 overflow-hidden" style={FALLBACK_GRADIENTS[imageKey]}>
            <img
                src={image}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4"><Tag>{tag}</Tag></div>
        </div>
        <div className="p-6">
            <div className="text-2xl font-serif text-amber-200 mb-1">{title}</div>
            <div className="text-sm text-neutral-400 mb-4">{sub}</div>
            <ul className="space-y-2 text-sm text-neutral-400">
                {points.map((p, i) => (
                    <li key={i} className="flex gap-2">
                        <span className="text-amber-400">·</span>
                        <span>{p}</span>
                    </li>
                ))}
            </ul>
        </div>
    </div>
);

const MockupScreen = ({ image, imageKey, title, caption, gradient = "from-amber-500/20 to-black/60" }) => (
    <div className="relative rounded-3xl overflow-hidden border border-neutral-800 bg-neutral-950">
        <div className="relative h-96" style={FALLBACK_GRADIENTS[imageKey]}>
            <img
                src={image}
                alt={title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className={`absolute inset-0 bg-gradient-to-b ${gradient}`} />
            <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300 mb-2">{title}</div>
                <div className="text-2xl md:text-3xl font-serif text-white leading-tight">{caption}</div>
            </div>
        </div>
    </div>
);

export default function YatraPreviewPage() {
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);

    return (
        <div data-testid="yatra-preview-page" className="min-h-screen bg-black text-neutral-200 overflow-x-hidden">

            {/* HERO */}
            <div className="relative h-screen min-h-[700px] flex items-center justify-center" style={FALLBACK_GRADIENTS.hero}>
                <div className="absolute inset-0">
                    <img
                        src={IMG.hero}
                        alt="Kashi ghats"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black" />
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    className="relative z-10 text-center px-6 max-w-4xl"
                >
                    <div className="mb-6"><Tag>Reset Sanctuary presents</Tag></div>
                    <div data-testid="yatra-hero-title" className="text-6xl md:text-8xl lg:text-9xl font-serif tracking-tight text-amber-100 mb-6">
                        यात्रा
                    </div>
                    <div className="text-2xl md:text-4xl font-serif text-white/90 mb-8">YATRA</div>

                    {/* TRINITY — big, unmissable */}
                    <div data-testid="yatra-trinity" className="mb-8">
                        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-3xl md:text-5xl lg:text-6xl font-serif tracking-tight leading-tight">
                            <span className="text-amber-200">AI Cinema</span>
                            <span className="text-amber-500/60 text-2xl md:text-4xl">+</span>
                            <span className="text-blue-200">The Game</span>
                            <span className="text-amber-500/60 text-2xl md:text-4xl">+</span>
                            <span className="text-fuchsia-200">Astrology World</span>
                        </div>
                        <div className="mt-4 text-[11px] md:text-xs tracking-[0.35em] uppercase text-amber-300/80">
                            One product · three worlds · in your browser
                        </div>
                    </div>

                    <p className="text-base md:text-lg text-neutral-300 max-w-2xl mx-auto leading-relaxed">
                        The first photoreal AI-cinema pilgrimage — that becomes a walkable game — powered by your Vedic Kundli.
                    </p>
                    <div className="mt-12 flex items-center justify-center gap-2 text-[10px] tracking-[0.4em] uppercase text-amber-300/70">
                        <span>V1 Cinema · October 2026</span>
                        <span className="opacity-40">·</span>
                        <span>V2 Game · November 2026</span>
                    </div>
                </motion.div>
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-neutral-500 text-xs tracking-widest uppercase animate-pulse">Scroll ↓</div>
            </div>

            {/* TRINITY STRIP — three worlds, one product */}
            <div data-testid="yatra-trinity-strip" className="border-y border-amber-500/20 bg-gradient-to-b from-black via-neutral-950 to-black py-16 md:py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <div className="text-[10px] md:text-xs tracking-[0.4em] uppercase text-amber-300/80 mb-3">One subscription · Three worlds</div>
                        <h2 className="text-3xl md:text-5xl font-serif text-amber-100">YATRA is not one product. It's three.</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="p-8 rounded-2xl bg-neutral-950 border border-amber-500/30 hover:border-amber-500/60 transition-colors">
                            <div className="text-[10px] tracking-[0.35em] uppercase text-amber-300 mb-4">World 01 · V1 · Oct 2026</div>
                            <div className="text-3xl md:text-4xl font-serif text-amber-200 mb-3">AI Cinema</div>
                            <p className="text-sm text-neutral-400 leading-relaxed">
                                Photoreal, interactive pilgrimage films. Every scene generated for you — Kashi at sunrise, Kedarnath in monsoon, Char Dham on foot. 5-10 min daily.
                            </p>
                        </div>
                        <div className="p-8 rounded-2xl bg-neutral-950 border border-blue-500/30 hover:border-blue-500/60 transition-colors">
                            <div className="text-[10px] tracking-[0.35em] uppercase text-blue-300 mb-4">World 02 · V2 · Nov 2026</div>
                            <div className="text-3xl md:text-4xl font-serif text-blue-200 mb-3">The Game</div>
                            <p className="text-sm text-neutral-400 leading-relaxed">
                                One month after launch, every photoreal scene becomes walkable. First-person 3D pilgrimage powered by Genie 3 / World Models. Char Dham as a playable world.
                            </p>
                        </div>
                        <div className="p-8 rounded-2xl bg-neutral-950 border border-fuchsia-500/30 hover:border-fuchsia-500/60 transition-colors">
                            <div className="text-[10px] tracking-[0.35em] uppercase text-fuchsia-300 mb-4">World 03 · Live from Day One</div>
                            <div className="text-3xl md:text-4xl font-serif text-fuchsia-200 mb-3">Astrology World</div>
                            <p className="text-sm text-neutral-400 leading-relaxed">
                                Your Vedic Kundli drives the story. Dasha, nakshatra, transits — the yatra you get is calibrated to your chart. Not generic content. Not entertainment. A karmic map.
                            </p>
                        </div>
                    </div>
                    <div className="text-center mt-10">
                        <p className="text-sm text-neutral-500 max-w-2xl mx-auto">
                            Every subscriber gets all three. Cinema now. Game next. Astrology weaves through both. Zero rebuild between versions.
                        </p>
                    </div>
                </div>
            </div>

            {/* THE VOID */}
            <Section className="relative">
                <Reveal>
                    <div className="text-center mb-16">
                        <Tag>The Void</Tag>
                        <h2 className="text-4xl md:text-6xl font-serif text-amber-200 mt-6 mb-4">Nobody has built this</h2>
                        <p className="text-lg text-neutral-400 max-w-3xl mx-auto">
                            Five categories exist in isolation. Their intersection has zero products. That's the slot YATRA occupies.
                        </p>
                    </div>
                </Reveal>
                <div className="grid md:grid-cols-2 gap-4">
                    {[
                        { cat: "Photoreal AI Cinema", ex: "Runway · Sora · Pika", flaw: "Creator tools · not consumer products" },
                        { cat: "Interactive Choice Narrative", ex: "Choices · Episode · AI Dungeon", flaw: "Stylized only · no AI cinema · no photoreal" },
                        { cat: "Wellness Content", ex: "Calm · Headspace · Insight Timer", flaw: "Audio only · no visuals · no interactivity" },
                        { cat: "Vedic Astrology", ex: "AstroTalk · AstroSage", flaw: "Human marketplace or text · no cinema" },
                        { cat: "Browser Delivery (PWA)", ex: "Meta Quest · Steam · App Store", flaw: "Need headset · download · store approval" },
                    ].map((v, i) => (
                        <Reveal key={i} delay={i * 0.1}>
                            <div className="p-6 rounded-2xl bg-neutral-950 border border-neutral-800">
                                <div className="text-amber-300 text-xs tracking-widest uppercase mb-2">{v.cat}</div>
                                <div className="text-neutral-300 text-sm mb-2">Closest: <span className="text-white">{v.ex}</span></div>
                                <div className="text-red-400/80 text-sm">✗ {v.flaw}</div>
                            </div>
                        </Reveal>
                    ))}
                    <Reveal delay={0.5}>
                        <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/20 to-neutral-950 border border-amber-500/40 h-full flex flex-col justify-center">
                            <div className="text-amber-300 text-xs tracking-widest uppercase mb-2">The Intersection</div>
                            <div className="text-white text-2xl font-serif">YATRA</div>
                            <div className="text-neutral-400 text-sm mt-2">First mover in a new category.</div>
                        </div>
                    </Reveal>
                </div>
            </Section>

            {/* THE 3 PILLARS */}
            <Section className="bg-gradient-to-b from-black via-neutral-950 to-black">
                <Reveal>
                    <div className="text-center mb-16">
                        <Tag>The Product</Tag>
                        <h2 className="text-4xl md:text-6xl font-serif text-amber-200 mt-6 mb-4">Three pillars · one app</h2>
                        <p className="text-lg text-neutral-400 max-w-3xl mx-auto">
                            Your real Vedic chart · Your daily photoreal pilgrimage · Your wellness practice. All connected.
                        </p>
                    </div>
                </Reveal>
                <div className="grid md:grid-cols-3 gap-6">
                    <Reveal delay={0.1}>
                        <PillarCard
                            tag="Pillar 1 · Kundli"
                            title="Your Chart"
                            sub="Real Swiss Ephemeris Vedic math"
                            image={IMG.chart}
                            imageKey="chart"
                            points={[
                                "Full Vedic natal chart at onboarding",
                                "Nakshatra · Rashi · Dashas · transits",
                                "Persists across all app features",
                                "Production-grade astronomical accuracy",
                            ]}
                        />
                    </Reveal>
                    <Reveal delay={0.2}>
                        <PillarCard
                            tag="Pillar 2 · Yatra"
                            title="Your Journey"
                            sub="Photoreal AI-cinema · daily 5-10 min"
                            image={IMG.kashi}
                            imageKey="kashi"
                            points={[
                                "Scenes generated fresh by Gemini Nano Banana",
                                "10-sec cinematics via Sora 2 at peak moments",
                                "Aditya's voice-cloned narration (ElevenLabs)",
                                "Choice-driven · story adapts to you",
                            ]}
                        />
                    </Reveal>
                    <Reveal delay={0.3}>
                        <PillarCard
                            tag="Pillar 3 · Sadhana"
                            title="Your Practice"
                            sub="Wellness · ritual · music therapy"
                            image={IMG.meditation}
                            imageKey="meditation"
                            points={[
                                "Guided meditations synced to transits",
                                "Real-calendar festival triggers",
                                "AI-reflective journaling",
                                "Tatva K raga library integration",
                            ]}
                        />
                    </Reveal>
                </div>
            </Section>

            {/* PRODUCT MOCKUPS */}
            <Section>
                <Reveal>
                    <div className="text-center mb-16">
                        <Tag>The Feel</Tag>
                        <h2 className="text-4xl md:text-6xl font-serif text-amber-200 mt-6 mb-4">Every scene · production-film quality</h2>
                        <p className="text-lg text-neutral-400 max-w-3xl mx-auto">
                            AI generation collapsed the cost of photoreal from $15K per hand-crafted scene to ~$0.10 per generation.
                            The window opened in 2026. We're the first consumer product through it.
                        </p>
                    </div>
                </Reveal>
                <div className="grid md:grid-cols-2 gap-6">
                    <Reveal delay={0.1}>
                        <MockupScreen
                            image={IMG.ganga}
                            imageKey="ganga"
                            title="Morning · Session 1"
                            caption='"Welcome, pilgrim. Today, the Ganga rises with Krittika. Sit for a moment."'
                        />
                    </Reveal>
                    <Reveal delay={0.2}>
                        <MockupScreen
                            image={IMG.himalaya}
                            imageKey="himalaya"
                            title="Golden Hour · Foothills"
                            caption='"The path forks. Do you walk with the elder, or sit with the young student?"'
                            gradient="from-amber-500/10 to-black/70"
                        />
                    </Reveal>
                    <Reveal delay={0.3}>
                        <MockupScreen
                            image={IMG.diya}
                            imageKey="diya"
                            title="Festival Night · Diwali"
                            caption='"A thousand diyas rise from the river. Yours is one of them."'
                            gradient="from-orange-500/20 to-black/70"
                        />
                    </Reveal>
                    <Reveal delay={0.4}>
                        <MockupScreen
                            image={IMG.temple}
                            imageKey="temple"
                            title="Sacred Ruin · Dusk"
                            caption='"An old temple. A story waits inside — but only if you offer it silence."'
                            gradient="from-purple-500/10 to-black/70"
                        />
                    </Reveal>
                    <Reveal delay={0.5}>
                        <MockupScreen
                            image={IMG.kedarnath}
                            imageKey="kedarnath"
                            title="Kedarnath · Dawn"
                            caption='"You have climbed for three days. The temple stands. So do you."'
                            gradient="from-blue-500/10 to-black/70"
                        />
                    </Reveal>
                    <Reveal delay={0.6}>
                        <MockupScreen
                            image={IMG.mustard}
                            imageKey="mustard"
                            title="Golden Fields · Punjab"
                            caption='"A single figure walking. The horizon is honey. You are not lost — you are arriving."'
                            gradient="from-yellow-500/10 to-black/70"
                        />
                    </Reveal>
                </div>
            </Section>

            {/* CHAR DHAM */}
            <Section>
                <Reveal>
                    <div className="text-center mb-16">
                        <Tag>Char Dham</Tag>
                        <h2 className="text-4xl md:text-6xl font-serif text-amber-200 mt-6 mb-4">The four sacred sites</h2>
                        <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
                            Uttarakhand's Chota Char Dham. Each rendered photoreal. Each with its own narrative arc. Walk them across seasons — every visit generates fresh.
                        </p>
                    </div>
                </Reveal>
                <div className="grid md:grid-cols-2 gap-6">
                    <Reveal delay={0.1}>
                        <MockupScreen image={IMG.kedarnath} imageKey="kedarnath" title="Kedarnath · Shiva"
                            caption='"Three days of climb. The temple stands. So do you."' gradient="from-blue-500/10 to-black/75" />
                    </Reveal>
                    <Reveal delay={0.2}>
                        <MockupScreen image={IMG.badrinath} imageKey="badrinath" title="Badrinath · Vishnu"
                            caption='"Painted walls · icy river · hot spring at your feet. Vishnu waits."' gradient="from-orange-500/15 to-black/75" />
                    </Reveal>
                    <Reveal delay={0.3}>
                        <MockupScreen image={IMG.gangotri} imageKey="gangotri" title="Gangotri · Ganga"
                            caption='"Where the river begins. Marble against turquoise. Silence louder than the current."' gradient="from-cyan-500/10 to-black/75" />
                    </Reveal>
                    <Reveal delay={0.4}>
                        <MockupScreen image={IMG.yamunotri} imageKey="yamunotri" title="Yamunotri · Yamuna"
                            caption='"The hot spring steams · the goddess watches · rest before the last climb."' gradient="from-green-500/10 to-black/75" />
                    </Reveal>
                </div>
            </Section>

            {/* ROADMAP · V2 */}
            <Section className="bg-gradient-to-b from-black via-neutral-950 to-black">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                    <Reveal>
                        <div className="rounded-3xl overflow-hidden border border-blue-500/25 relative" style={FALLBACK_GRADIENTS.kedarnath}>
                            <img
                                src={IMG.kedarnath}
                                alt="Kedarnath walkable v2"
                                className="w-full h-96 object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />
                            <div className="absolute top-4 left-4">
                                <span className="inline-block px-3 py-1 rounded-full text-[10px] tracking-[0.3em] uppercase bg-blue-500/20 text-blue-300 border border-blue-500/40">
                                    November 2026 · One Month After Launch
                                </span>
                            </div>
                            <div className="absolute bottom-6 left-6 right-6">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-blue-300 mb-2">V2 · The Game · Nov 2026</div>
                                <div className="text-2xl md:text-3xl font-serif text-white leading-tight">Step into Kedarnath — not watch it. Walk it. Play it.</div>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <Tag>Roadmap · v2 game</Tag>
                        <h2 className="text-4xl md:text-5xl font-serif text-amber-200 mt-6 mb-6">2027 · YATRA becomes a game we build</h2>
                        <p className="text-lg text-neutral-400 mb-6">
                            Google DeepMind's Genie 3, OpenAI World Models, and Runway Aleph are shipping walkable AI worlds in 2026-27. When those APIs open, we plug YATRA's entire photoreal library straight into them — <span className="text-white">every place we've created becomes a real 3D game world you walk through, first person</span>. Same tight team. No engine hire. No 3-year AAA cycle. Just plumbing.
                        </p>
                        <div className="space-y-3 text-sm text-neutral-400">
                            <div className="flex gap-3 items-start">
                                <span className="text-blue-400">→</span>
                                <span><span className="text-white">V1 · October 2026</span> · Interactive photoreal cinema · make choices · scenes generate for you</span>
                            </div>
                            <div className="flex gap-3 items-start">
                                <span className="text-blue-400">→</span>
                                <span><span className="text-white">V2 · 2027 · The Game</span> · walk INTO every scene · first-person 3D pilgrimage · Char Dham as playable world · <span className="text-amber-200">buildable by us · no engine team</span></span>
                            </div>
                            <div className="flex gap-3 items-start">
                                <span className="text-blue-400">→</span>
                                <span><span className="text-white">V3 · 2028+</span> · Persistent shared pilgrimage · walk alongside other users at Kashi at sunrise · Guild multiplayer mode</span>
                            </div>
                        </div>
                        <p className="text-sm text-neutral-500 mt-6">
                            Every subscriber gets every version. Zero rebuild. Cinema now · game next · full world after. The AI progression is the roadmap.
                        </p>
                    </Reveal>
                </div>
            </Section>

            {/* PRICING */}
            <Section className="bg-gradient-to-b from-black via-neutral-950 to-black">
                <Reveal>
                    <div className="text-center mb-16">
                        <Tag>Pricing</Tag>
                        <h2 className="text-4xl md:text-6xl font-serif text-amber-200 mt-6 mb-4">Simple. Transparent.</h2>
                        <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
                            One free daily journey to try. Full access unlocks the whole path.
                        </p>
                        <div className="mt-6 inline-block px-4 py-2 rounded-full border border-amber-500/30 bg-amber-500/5">
                            <span className="text-[10px] tracking-[0.3em] uppercase text-amber-300">Illustrative pricing · final tiers TBD at launch</span>
                        </div>
                    </div>
                </Reveal>
                <div className="grid md:grid-cols-4 gap-4">
                    <Reveal delay={0.1}>
                        <div className="p-6 rounded-2xl bg-neutral-950 border border-neutral-800 h-full">
                            <div className="text-xs text-neutral-500 uppercase tracking-widest mb-3">Free</div>
                            <div className="text-3xl font-serif text-neutral-200">Taste</div>
                            <div className="text-xs text-neutral-500 mt-1">no card · forever</div>
                            <ul className="mt-6 space-y-2 text-sm text-neutral-400">
                                <li>· 1 daily yatra</li>
                                <li>· Text chart summary</li>
                                <li>· Basic reflections</li>
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/15 to-neutral-950 border border-amber-500/40 h-full relative">
                            <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-amber-500 text-black text-[10px] font-bold uppercase tracking-widest">Most Popular</div>
                            <div className="text-xs text-amber-300 uppercase tracking-widest mb-3">Premium</div>
                            <div className="text-3xl font-serif text-amber-200">Full Access</div>
                            <div className="text-xs text-neutral-500 mt-1">the whole path</div>
                            <ul className="mt-6 space-y-2 text-sm text-neutral-400">
                                <li>· Unlimited yatras</li>
                                <li>· Full photoreal + video + voice</li>
                                <li>· Deep kundli breakdown</li>
                                <li>· Full Sadhana library</li>
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <div className="p-6 rounded-2xl bg-neutral-950 border border-neutral-800 h-full">
                            <div className="text-xs text-neutral-500 uppercase tracking-widest mb-3">Elite</div>
                            <div className="text-3xl font-serif text-neutral-200">Guided</div>
                            <div className="text-xs text-neutral-500 mt-1">for the serious seeker</div>
                            <ul className="mt-6 space-y-2 text-sm text-neutral-400">
                                <li>· Everything in Premium</li>
                                <li>· Monthly audio note from Aditya</li>
                                <li>· Weekly personal forecast audio</li>
                                <li>· Priority feature access</li>
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.4}>
                        <div className="p-6 rounded-2xl bg-neutral-950 border border-neutral-800 h-full">
                            <div className="text-xs text-neutral-500 uppercase tracking-widest mb-3">Family</div>
                            <div className="text-3xl font-serif text-neutral-200">Shared</div>
                            <div className="text-xs text-neutral-500 mt-1">up to 5 accounts</div>
                            <ul className="mt-6 space-y-2 text-sm text-neutral-400">
                                <li>· Everything in Premium</li>
                                <li>· 5 individual profiles</li>
                                <li>· Family transit calendar</li>
                                <li>· Shared festival mode</li>
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </Section>

            {/* CTA */}
            <Section className="text-center">
                <Reveal>
                    <Tag>Get Early Access</Tag>
                    <h2 className="text-4xl md:text-5xl font-serif text-amber-200 mt-6 mb-4">The first pilgrimage · yours in October</h2>
                    <p className="text-neutral-400 max-w-xl mx-auto mb-8">Reserve your spot on the launch list. First 500 users get a lifetime founder rate.</p>
                    <div className="max-w-md mx-auto flex flex-col md:flex-row gap-3">
                        <input
                            data-testid="yatra-cta-email"
                            type="email"
                            placeholder="Your email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="flex-1 px-5 py-3 rounded-full bg-neutral-950 border border-neutral-800 text-neutral-200 placeholder-neutral-600 focus:border-amber-500/50 focus:outline-none"
                        />
                        <button
                            data-testid="yatra-cta-submit"
                            onClick={() => setSubmitted(true)}
                            className="px-6 py-3 rounded-full bg-amber-500 text-black font-semibold hover:bg-amber-400 transition"
                        >
                            {submitted ? "✓ You're on the list" : "Join the Waitlist"}
                        </button>
                    </div>
                </Reveal>
                <div className="mt-16 text-[10px] tracking-[0.4em] uppercase text-neutral-600">
                    Reset Sanctuary · 2026 · Coming October
                </div>
            </Section>
        </div>
    );
}
